import {areSetsEqual} from '@benchling/js-utils';

import {PipelineGraphQL} from 'pipelines/PipelineTypes';

function isLegacyPipelineStepConsistent(
  stepIds: Set<string>,
  step: PipelineGraphQL['execution']['steps'][number]
): boolean {
  return (
    step.definition.incomingEdges.every((edge) => stepIds.has(edge.parent.step.id)) &&
    step.definition.outgoingEdges.every((edge) => stepIds.has(edge.child.step.id))
  );
}

function isPipelineStepConsistent(stepIds: Set<string>, step: PipelineGraphQL['steps'][number]): boolean {
  return (
    step.incomingEdges.every((edge) => stepIds.has(edge.fromStep.id)) &&
    step.outgoingEdges.every((edge) => stepIds.has(edge.toStep.id))
  );
}

function isPipelineViewConsistent(stepIds: Set<string>, view: PipelineGraphQL['views'][number]): boolean {
  return view.lineage.every(({step}) => stepIds.has(step.id));
}

/**
 * Occasionally, if the pipeline is updated as the GraphQL query response is being generated on the backend,
 * the GraphQL response may be internally inconsistent, until the next re-fetch.
 *
 * Currently, Benchling's GraphQL endpoint does not guarantee internal consistency; to do so, it would have
 * to use (or expose an option for) REPEATABLE READ consistency.
 *
 * In particular, `PipelineStep.outgoingEdges` may reference steps that do not exist in
 * `PipelineGraphQL.steps`.
 *
 * To mitigate user impact while we investigate the underlying cause of these inconsistencies, we can
 * detect these inconsistencies on the front-end.
 *
 * @package
 */
export default function isPipelineGraphQLInternallyConsistent(pipeline: PipelineGraphQL): boolean {
  const stepIds = new Set(pipeline.steps.map((step) => step.id));
  const stepIdsInExecution = new Set(pipeline.execution.steps.map((step) => step.id));

  if (!areSetsEqual(stepIds, stepIdsInExecution)) {
    return false;
  }

  if (!pipeline.execution.steps.every((step) => isLegacyPipelineStepConsistent(stepIds, step))) {
    return false;
  }

  if (!pipeline.steps.every((step) => isPipelineStepConsistent(stepIds, step))) {
    return false;
  }

  if (!pipeline.views.every((view) => isPipelineViewConsistent(stepIds, view))) {
    return false;
  }

  return true;
}
